import { Injectable } from '@angular/core';
import * as protobuf from 'protobufjs';
import Delaunator from 'delaunator';

export interface LazTerrainResult {
  protoBinary: Uint8Array;
  vertexCount: number;
  triangleCount: number;
}

@Injectable({
  providedIn: 'root'
})
export class LazToTerrainService {
  private root: protobuf.Root | null = null;
  private TerrainsType: protobuf.Type | null = null;

  constructor() {
    this.initializeProtobuf();
  }

  private async initializeProtobuf(): Promise<void> {
    try {
      this.root = await protobuf.load([
        'assets/proto/common.proto',
        'assets/proto/terrain.proto'
      ]);
      this.TerrainsType = this.root.lookupType('epc.vertex.Terrains');
    } catch (error) {
      console.error('Failed to load protobuf definitions:', error);
    }
  }

  private async ensureInitialized(): Promise<void> {
    if (!this.TerrainsType) {
      await this.initializeProtobuf();
    }
  }

  /**
   * Convertit un nuage de points LAZ (positions + couleurs) en proto Terrain triangulé.
   *
   * @param positions Float32Array de positions XYZ relatives au centre
   * @param colors Float32Array de couleurs RGB [0-1]
   * @param count Nombre de points
   * @param centerX Centre X absolu du nuage (coordonnées site)
   * @param centerY Centre Y absolu
   * @param centerZ Centre Z absolu
   * @param siteId ID du site
   * @param epsgCode Code EPSG du site
   */
  async convertToTerrain(
    positions: Float32Array,
    colors: Float32Array,
    count: number,
    centerX: number,
    centerY: number,
    centerZ: number,
    siteId: number,
    epsgCode: number
  ): Promise<LazTerrainResult> {
    await this.ensureInitialized();

    if (!this.TerrainsType) {
      throw new Error('Protobuf not initialized');
    }

    console.log(`[LAZ→Terrain] Triangulating ${count} points...`);
    const startTime = performance.now();

    // Extraire les coordonnées 2D (X, Y) pour Delaunay
    const coords = new Float64Array(count * 2);
    for (let i = 0; i < count; i++) {
      coords[i * 2] = positions[i * 3];       // X
      coords[i * 2 + 1] = positions[i * 3 + 1]; // Y
    }

    // Triangulation Delaunay 2D
    const delaunay = new Delaunator(coords);
    const triangleCount = delaunay.triangles.length / 3;

    console.log(`[LAZ→Terrain] ${triangleCount} triangles in ${Math.round(performance.now() - startTime)}ms`);

    // Construire les points en coordonnées absolues (Vec3d = double)
    const points: { x: number; y: number; z: number }[] = [];
    for (let i = 0; i < count; i++) {
      points.push({
        x: positions[i * 3] + centerX,
        y: positions[i * 3 + 1] + centerY,
        z: positions[i * 3 + 2] + centerZ
      });
    }

    // Construire les couleurs (Vec3 = float)
    const meshColors: { x: number; y: number; z: number }[] = [];
    for (let i = 0; i < count; i++) {
      meshColors.push({
        x: colors[i * 3],
        y: colors[i * 3 + 1],
        z: colors[i * 3 + 2]
      });
    }

    // Construire les indices
    const indices = Array.from(delaunay.triangles);

    // Encoder en proto
    const terrainMessage = {
      uuid: crypto.randomUUID(),
      terrains: [{
        uuid: crypto.randomUUID(),
        name: 'LAZ Point Cloud',
        mesh: {
          points,
          colors: meshColors,
          indices,
          normals: [],
          normalMap: [],
          texCoords: [],
          tangents: [],
          patchedPoints: [],
          nbFacesPerMaterialId: {}
        },
        overlayGeometry: null,
        materials: [],
        epsgCode,
        terrainType: 0 // GALLERY
      }],
      offset: { x: centerX, y: centerY, z: centerZ },
      siteId,
      blastId: 0
    };

    const errMsg = this.TerrainsType.verify(terrainMessage);
    if (errMsg) {
      console.warn('[LAZ→Terrain] Proto verification warning:', errMsg);
    }

    const message = this.TerrainsType.create(terrainMessage);
    const protoBinary = this.TerrainsType.encode(message).finish();

    console.log(`[LAZ→Terrain] Proto size: ${(protoBinary.length / 1024 / 1024).toFixed(1)} MB`);

    return {
      protoBinary,
      vertexCount: count,
      triangleCount
    };
  }
}
